import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDownToLine, ArrowLeft, ArrowLeftRight, ArrowUpFromLine, CircleAlert, ReceiptText } from 'lucide-react'
import { PortalShell } from '@/components/portal/PortalShell'
import { useClientId } from '@/hooks/useClientId'
import { clientApi } from '@/lib/api-client'
import { formatCurrency, formatDateTime } from '@/lib/utils'

function TypeIcon({ type }: { type: string }) {
  const upper = type.toUpperCase()
  if (upper.startsWith('DEPOSIT')) return <ArrowDownToLine className="prod-txn__icon-svg" aria-hidden="true" />
  if (upper.startsWith('WITHDRAW')) return <ArrowUpFromLine className="prod-txn__icon-svg" aria-hidden="true" />
  return <ArrowLeftRight className="prod-txn__icon-svg" aria-hidden="true" />
}

export function FundsPage() {
  const { t } = useTranslation()
  const { clientId, resolving } = useClientId()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['client', 'transactions', clientId],
    queryFn: () => clientApi.getTransactions(clientId!),
    enabled: Boolean(clientId),
  })

  const items = data?.items ?? []

  return (
    <PortalShell>
      <div className="prod-history">
        <Link to="/funding" className="prod-back-link">
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('nav.funding')}
        </Link>
        <header className="prod-history__header">
          <h1 className="prod-text-page-title prod-text-page-title--left">{t('funds.title')}</h1>
          <p className="prod-text-body">{t('funds.subtitle')}</p>
        </header>

        {resolving || isLoading ? (
          <div className="prod-txn-list" aria-busy="true">
            <span className="sr-only">{t('common.loading')}</span>
            {[0, 1, 2].map((i) => (
              <div key={i} className="prod-txn prod-txn--skeleton" aria-hidden="true">
                <span className="prod-skeleton prod-skeleton--icon" />
                <span className="prod-skeleton prod-skeleton--line" />
                <span className="prod-skeleton prod-skeleton--line-sm" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="prod-inline-error" role="alert">
            <CircleAlert className="prod-inline-error__icon" aria-hidden="true" />
            <p className="prod-inline-error__text">{t('funds.error')}</p>
            <button type="button" className="prod-btn prod-btn--outline prod-btn--compact" onClick={() => void refetch()}>
              {t('common.retry')}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="prod-empty">
            <ReceiptText className="prod-empty__icon" aria-hidden="true" />
            <p className="prod-empty__title">{t('funds.empty')}</p>
            <p className="prod-empty__text">{t('funds.emptyHint')}</p>
            <Link to="/funding?tab=deposit" className="prod-btn prod-btn--outline">
              {t('fundingPage.submitDeposit')}
            </Link>
          </div>
        ) : (
          <ul className="prod-txn-list">
            {items.map((item) => {
              const typeLabel = t(`funds.types.${item.type}`, { defaultValue: item.type })
              const statusLabel = t(`funds.statuses.${item.status}`, { defaultValue: item.status })
              return (
                <li key={item.id} className="prod-txn">
                  <span className="prod-txn__icon" data-type={item.type}>
                    <TypeIcon type={item.type} />
                  </span>
                  <div className="prod-txn__main">
                    <p className="prod-txn__type">{typeLabel}</p>
                    <p className="prod-txn__meta">
                      <span className="prod-num">
                        {item.executed_at
                          ? formatDateTime(item.executed_at)
                          : t('funds.requestedAt', { date: formatDateTime(item.created_at) })}
                      </span>
                      <span className="prod-txn__account">{t('funds.accountLine', { id: item.login_id })}</span>
                    </p>
                    {item.comment ? <p className="prod-txn__comment">{item.comment}</p> : null}
                  </div>
                  <div className="prod-txn__side">
                    <span className="prod-txn__amount">{formatCurrency(item.amount, item.currency)}</span>
                    <span className="prod-status-badge" data-status={item.status}>
                      {statusLabel}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </PortalShell>
  )
}
